import { EditOutlined } from "@ant-design/icons";
import { Space, type TableColumnsType, type TableProps, Tooltip } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Table } from "@av/components/atoms/table/Table";
import {
  type AdminSettingsUserBase,
  headerCell,
} from "../../../../../services/admin-settings/type";

import type { TFunction } from "i18next";

type ColumnsType<T> = NonNullable<TableProps<T>["columns"]>;

type AdminSettingsUserTableProps<TUser extends AdminSettingsUserBase> = {
  users: TUser[];
  dataColumns: ColumnsType<TUser>;
  onEditUser?: (user: TUser) => void;
  isEditUserPending?: (user: TUser) => boolean;
  isEditUserLongPending?: (user: TUser) => boolean;
  isEditUserTimedOut?: (user: TUser) => boolean;
  editUserPendingLabel?: string;
  editUserLongPendingLabel?: string;
  editUserTimedOutLabel?: string;
  editUserPendingMessage?: (user: TUser) => string | undefined;
  getTestIdValue?: (user: TUser) => string;
  testIdPrefix: string;
  isLoading?: boolean;
  t: TFunction;
};

const useStyles = createStyles(({ css, token }) => ({
  actionDeleteIcon: css`
    color: ${token.colorTextTertiary};
  `,
  actionEditIcon: css`
    color: ${token.colorPrimary};
  `,
}));

export function CommonUserTable<TUser extends AdminSettingsUserBase>(
  props: AdminSettingsUserTableProps<TUser>
) {
  const {
    testIdPrefix,
    dataColumns,
    users,
    onEditUser,
    isEditUserPending,
    isEditUserLongPending,
    isEditUserTimedOut,
    editUserPendingLabel,
    editUserLongPendingLabel,
    editUserTimedOutLabel,
    editUserPendingMessage,
    getTestIdValue = (user) => user.userId,
    isLoading,
    t,
  } = props;

  const { styles } = useStyles();
  const actionColumn: TableColumnsType<TUser> = [
    {
      title: t("platform.adminSettings.users_columns_action"),
      key: "action",
      align: "center",
      onHeaderCell: headerCell(`${testIdPrefix}-column-action`),
      width: 140,
      render: (_, user) => {
        const isPending = isEditUserPending?.(user) === true;
        const isLongPending = isEditUserLongPending?.(user) === true;
        const isTimedOut = isEditUserTimedOut?.(user) === true;
        const editButton = (
          <Button
            aria-label={t("platform.adminSettings.users_actions_edit", { name: user.name })}
            disabled={!onEditUser || isPending}
            loading={isPending && !isTimedOut}
            icon={<EditOutlined className={styles.actionEditIcon} />}
            testIdPrefix={`${testIdPrefix}-edit-${getTestIdValue(user)}`}
            type="text"
            onClick={() => onEditUser?.(user)}
          >
            {isTimedOut
              ? editUserTimedOutLabel
              : isPending
                ? isLongPending
                  ? editUserLongPendingLabel
                  : editUserPendingLabel
                : undefined}
          </Button>
        );
        const pendingMessage = editUserPendingMessage?.(user);

        return (
          <Space>
            {pendingMessage ? (
              <Tooltip title={pendingMessage} trigger={["hover", "focus"]}>
                <span>{editButton}</span>
              </Tooltip>
            ) : (
              editButton
            )}
          </Space>
        );
      },
    },
  ];

  const totalColumns = [...dataColumns, ...actionColumn];

  return (
    <div aria-busy={isLoading} data-testid={`${testIdPrefix}-table-loading-region`}>
      <Table
        columns={totalColumns}
        dataSource={users}
        loading={isLoading}
        pagination={{
          defaultPageSize: 5,
          pageSizeOptions: [5, 10, 25, 50, 100],
        }}
        rowKey="userId"
        sortDirections={["ascend", "descend", "ascend"]}
        testIdPrefix={testIdPrefix}
      />
    </div>
  );
}
